import React, { useState, useMemo, useCallback, useEffect } from "react";
import { connect } from "react-redux";
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonButtons,
  IonButton,
  IonTitle,
  IonContent,
  IonItem,
  IonLabel,
  IonInput,
  IonNote,
  IonCheckbox,
  IonSearchbar,
  IonList,
  IonFooter,
  useIonAlert,
  IonText,
} from "@ionic/react";
import Spacer from "components/spacer";
import { WSemit } from "services/socket";
import { addTeam, registerTeam } from "services/teams";
import { obfuscateEmail } from "services/util";
import { debounce } from "lodash";

interface props {
  teams: any;
  allTeams: any;
  quizConfig: any;
  addTeam: Function;
  registerTeam: Function;
  user: any;
  quiz: any;
}

interface validation {
  [key: string]: boolean | undefined;
}

const AddTeamModal: React.FC<props> = ({
  teams,
  allTeams,
  quizConfig,
  addTeam,
  registerTeam,
  user,
  quiz,
}) => {
  const defaultState = {
    teamName: "",
    playerCount: 4,
    teamEmail: "",
    teamId: "",
    newTeam: false,
  };

  const [presentAlert] = useIonAlert();
  const [searchValue, setSearchValue] = useState("");
  const [team, setTeam] = useState(defaultState);
  const [teamList, setTeamList] = useState<any[]>([]);
  const [allSearchResults, setAllSearchResults] = useState<any[]>([]);
  const [displayedResultsCount, setDisplayedResultsCount] = useState(0);
  const [hasMoreResults, setHasMoreResults] = useState(false);
  const [isTouched, setIsTouched] = useState<validation>({});
  const [isValid, setIsValid] = useState<validation>({});
  const [selectedTeam, setSelectedTeam] = useState<number>(0);
  const [addModal, setAddModal] = useState(false);
  const { customerId } = user.userDetails;
  const { quizId } = quiz;

  const INITIAL_RESULTS_COUNT = 20;
  const LOAD_MORE_COUNT = 20;
  const MIN_SEARCH_LENGTH = 1;

  const searchIndex = useMemo(() => {
    return allTeams.map((team: any) => ({
      ...team,
      searchableTeamName:
        typeof team.teamName === "string" ? team.teamName.toLowerCase() : "",
      searchableTeamId:
        typeof team.teamId === "string"
          ? team.teamId.toLowerCase()
          : typeof team.teamId === "number"
          ? team.teamId.toString()
          : "",
    }));
  }, [allTeams]);

  const performSearch = useCallback(
    (searchValue: string) => {
      if (!searchValue || searchValue.length < MIN_SEARCH_LENGTH) {
        setTeamList([]);
        setAllSearchResults([]);
        setDisplayedResultsCount(0);
        setHasMoreResults(false);
        return;
      }

      const lowerSearchValue = searchValue.toLowerCase();
      const allResults: any[] = [];

      for (const team of searchIndex) {
        if (
          team.searchableTeamName.startsWith(lowerSearchValue) ||
          team.searchableTeamId.startsWith(lowerSearchValue)
        ) {
          allResults.push(team);
        }
      }

      setAllSearchResults(allResults);
      const initialResults = allResults.slice(0, INITIAL_RESULTS_COUNT);
      setTeamList(initialResults);
      setDisplayedResultsCount(INITIAL_RESULTS_COUNT);
      setHasMoreResults(allResults.length > INITIAL_RESULTS_COUNT);
    },
    [searchIndex]
  );

  const debouncedSearch = useMemo(
    () => debounce(performSearch, 500),
    [performSearch]
  );

  const loadMoreResults = useCallback(() => {
    const newDisplayCount = displayedResultsCount + LOAD_MORE_COUNT;
    const moreResults = allSearchResults.slice(0, newDisplayCount);
    setTeamList(moreResults);
    setDisplayedResultsCount(newDisplayCount);
    setHasMoreResults(allSearchResults.length > newDisplayCount);
  }, [allSearchResults, displayedResultsCount]);

  const validateEmail = (email: string) => {
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail || trimmedEmail.length === 0) return false;
    const atCount = (trimmedEmail.match(/@/g) || []).length;
    if (atCount !== 1) return false;
    const [localPart, domainPart] = trimmedEmail.split("@");
    if (!localPart || localPart.length === 0 || localPart.length > 64)
      return false;
    if (!domainPart || domainPart.length === 0 || domainPart.length > 253)
      return false;
    if (trimmedEmail.includes("..")) return false;
    if (trimmedEmail.startsWith(".") || trimmedEmail.endsWith("."))
      return false;
    if (trimmedEmail.startsWith("@") || trimmedEmail.endsWith("@"))
      return false;
    const emailRegex =
      /^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    const regexMatch = emailRegex.test(trimmedEmail);
    const domainParts = domainPart.split(".");
    const hasValidTLD =
      domainParts.length >= 2 &&
      domainParts[domainParts.length - 1].length >= 2;
    return regexMatch && hasValidTLD;
  };

  const validateInput = (field: string, value: string) => {
    setIsValid({ ...isValid, [field]: undefined });
    let validity = false;
    if (field === "teamEmail") {
      if (value.trim() === "") {
        setIsValid({ ...isValid, teamEmail: undefined });
        validity = true;
      } else {
        const isEmailValid = validateEmail(value as string);
        if (isEmailValid) {
          setIsValid({ ...isValid, teamEmail: true });
          validity = true;
        } else {
          setIsValid({ ...isValid, teamEmail: false });
        }
      }
    } else if (field === "playerCount") {
      if (!(parseInt(value) > 0)) {
        setIsValid({ ...isValid, playerCount: false });
      } else {
        setIsValid({ ...isValid, playerCount: true });
        validity = true;
      }
    } else if (field === "teamName") {
      if (value === "" || value.trim().length === 0) {
        setIsValid({ ...isValid, teamName: false });
      } else {
        validity = true;
        setIsValid({ ...isValid, teamName: true });
      }
    }
    return validity;
  };

  const markTouched = (e: any) => {
    let field = e.target.name;
    setIsTouched({ ...isTouched, [field]: true });
  };

  const onWillDismiss = (ev: any) => {
    clearSearch();
    clearFields();
    setAddModal(false);
  };

  const closeAddTeamModal = () => {
    onWillDismiss({});
  };

  const onClickAddTeam = () => {
    setAddModal(true);
  };

  const selectTeam = (id: number) => {
    let teamName = getTeamData(id).teamName;
    let count = checkTeamNameUnique(teamName);
    if (count > 1) {
      presentAlert({
        header: "Alert",
        subHeader: "Duplicate team name",
        message: `<p>This team name is used by multiple teams. Please check the email address to ensure you have selected the correct team.</p><p> Email:${obfuscateEmail(
          getTeamData(id).teamEmail
        )}</p>`,
        buttons: [
          "Cancel",
          {
            text: "OK",
            role: "confirm",
            handler: () => {
              setSelectedTeam(id);
            },
          },
        ],
      });
    } else {
      setSelectedTeam(id);
    }
  };

  const closeSelectedTeamModal = () => {
    setSelectedTeam(0);
  };

  const populateTeamData = (quizConfig: any) => {
    const rounds = Array(quizConfig.format.rounds).fill(null);
    let teamConfig: any = {};
    if (Boolean(quizConfig.format.hasBreaker)) {
      teamConfig.breaker = { answer: null, proximity: null, rank: null };
    }
    if (Boolean(quizConfig.format.isCrossword)) {
      teamConfig.puzzle = new Array(quizConfig.puzzle.questionCount).fill(null);
    }
    return { rounds, ...teamConfig };
  };

  const confirmTeam = () => {
    let selected = getTeamData(selectedTeam);
    if (validateInput("playerCount", team.playerCount.toString()) === false)
      return;
    let teamData = {
      teamName: selected.teamName,
      teamEmail: selected.teamEmail,
      teamId: selectedTeam,
      playerCount: team.playerCount,
      ...populateTeamData(quizConfig),
    };
    addTeam(teamData);
    createTeam(teamData);
    clearSearch();
    closeSelectedTeamModal();
    onWillDismiss({});
  };

  const createTeam = (teamData: any) => {
    if (validateInput("playerCount", team.playerCount.toString()) === false)
      return;
    const values = {
      teamData: JSON.stringify({
        teamName: teamData.teamName,
        teamEmail: teamData.teamEmail,
        teamId: teamData.teamId,
        playerCount: team.playerCount,
        ...populateTeamData(quizConfig),
      }),
      teamId: teamData.teamId,
      customerId,
      quizId,
      quizConfig,
    };
    WSemit("createTeam", values);
  };

  const onChange = (e: any) => {
    let field = e.target.name;
    let value = e.target.value;
    if (field === "playerCount") value = parseInt(value);
    validateInput(field, value);
    setTeam({ ...team, [field]: value });
  };

  const onCheckNewTeam = (e: any) => {
    let checked = e.target.checked;
    if (checked) {
      setTeam({ ...defaultState, newTeam: true });
    } else {
      setTeam({ ...team, newTeam: false });
    }
    clearSearch();
  };

  const checkTeamEmailUnique = (teamEmail: string) => {
    let unique = 0;
    allTeams.forEach((team: any) => {
      if (team.teamEmail === teamEmail) unique++;
    });
    return unique;
  };

  const checkTeamNameUnique = (teamName: string) => {
    let unique = 0;
    allTeams.forEach((team: any) => {
      if (team.teamName === teamName) unique++;
    });
    return unique;
  };

  const registerNewTeam = () => {
    const isPlayerCountValid = validateInput(
      "playerCount",
      team.playerCount.toString()
    );
    const isTeamNameValid = validateInput("teamName", team.teamName);
    const isEmailValid =
      team.teamEmail.trim() === ""
        ? true
        : validateInput("teamEmail", team.teamEmail);
    if (!isTeamNameValid) {
      presentAlert({
        header: "Validation Error",
        subHeader: "Team name required!",
        message: "Please enter a valid team name.",
        buttons: [{ text: "OK", role: "cancel" }],
      });
      return;
    }
    if (!isPlayerCountValid) {
      presentAlert({
        header: "Validation Error",
        subHeader: "Invalid player count!",
        message: "Please enter a valid player count (greater than 0).",
        buttons: [{ text: "OK", role: "cancel" }],
      });
      return;
    }
    if (team.teamEmail.trim() !== "" && !isEmailValid) {
      presentAlert({
        header: "Validation Error",
        subHeader: "Invalid email address!",
        message: "Please enter a valid email address or leave the field empty.",
        buttons: [{ text: "OK", role: "cancel" }],
      });
      return;
    }
    if (
      checkTeamNameUnique(team.teamName) > 0 &&
      team.teamEmail.trim() === ""
    ) {
      presentAlert({
        header: "Alert",
        subHeader: "Email required!",
        message:
          "A team with this name already exists. To use this team name an email address must be added to identify it. Either enter a unique email or change the team name.",
        buttons: [{ text: "Cancel", role: "cancel" }],
      });
      return;
    } else if (checkTeamNameUnique(team.teamName) > 0) {
      if (checkTeamEmailUnique(team.teamEmail) > 0) {
        presentAlert({
          header: "Alert",
          subHeader: "A team with the same name and email already exists!",
          message: "Please input another name or email.",
          buttons: [{ text: "OK", role: "cancel" }],
        });
        return;
      } else {
        handleRegister();
      }
    } else {
      handleRegister();
    }
  };

  const handleRegister = () => {
    registerTeam(
      {
        teamName: team.teamName,
        playerCount: team.playerCount,
        teamEmail: team.teamEmail,
      },
      quizConfig
    ).then((res: any) => {
      createTeam(res.team);
    });
    dismissRegister();
  };

  const dismissRegister = () => {
    clearSearch();
    clearFields();
    onWillDismiss({});
  };

  const clearSearch = () => {
    setSearchValue("");
    setTeamList([]);
    setAllSearchResults([]);
    setDisplayedResultsCount(0);
    setHasMoreResults(false);
  };

  const clearFields = () => {
    setTeam(defaultState);
  };

  // debounce search
  useEffect(() => {
    return () => {
      // Cleanup debounced function on unmount
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const onSearch = (e: any) => {
    const raw = e.target.value;
    setSearchValue(raw);
    debouncedSearch(raw.trim());
  };

  const getTeamData = (selectedTeam: number) => {
    if (selectedTeam) {
      return allTeams.find((t: any) => t.teamId === selectedTeam);
    }
  };

  const getTeamName = (selectedTeam: number) => {
    if (selectedTeam) {
      return getTeamData(selectedTeam)?.teamName;
    }
  };

  const alreadyRegistered = (teamId: number) => {
    let filtered = teams.teams.filter((team: any) => team.teamId === teamId);
    if (filtered.length > 0) return false;
    return true;
  };

  return (
    <>
      <IonButton onClick={onClickAddTeam}>Add Team</IonButton>
      <IonModal
        isOpen={Boolean(selectedTeam)}
        onWillDismiss={closeSelectedTeamModal}
        breakpoints={[1]}
        style={{ padding: "15px" }}
      >
        <IonHeader>
          <IonToolbar>
            <IonTitle>Add {getTeamName(selectedTeam)}?</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={closeSelectedTeamModal} color="light">
                Cancel
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          {alreadyRegistered(selectedTeam) ? (
            <IonItem
              className={`${isValid.playerCount && "ion-valid"} ${
                isValid.playerCount === false && "ion-invalid"
              } ${isTouched.playerCount && "ion-touched"}`}
            >
              <IonLabel position="floating">Player Count</IonLabel>
              <IonInput
                type="number"
                name="playerCount"
                placeholder="Player Count"
                value={team.playerCount}
                onIonChange={onChange}
                onIonBlur={markTouched}
              />
              <IonNote slot="helper">
                Enter the number of players on the team
              </IonNote>
              <IonNote slot="error">Invalid player count</IonNote>
            </IonItem>
          ) : (
            <p>This team is already registered for this event</p>
          )}
          <Spacer size="60" />
        </IonContent>
        {alreadyRegistered(selectedTeam) && (
          <IonFooter>
            <IonItem>
              <IonButtons slot="end">
                <IonButton color="primary" onClick={confirmTeam}>
                  Confirm
                </IonButton>
              </IonButtons>
            </IonItem>
          </IonFooter>
        )}
      </IonModal>
      <IonModal
        isOpen={addModal}
        onWillDismiss={(ev) => onWillDismiss(ev)}
        style={{ padding: "15px" }}
      >
        <IonHeader>
          <IonToolbar>
            <IonTitle>Add Team</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={closeAddTeamModal} color="light">
                Cancel
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <IonItem>
            <IonCheckbox
              slot="start"
              checked={team.newTeam}
              onIonChange={onCheckNewTeam}
            ></IonCheckbox>
            <IonLabel>Register New Team</IonLabel>
          </IonItem>
          {!team.newTeam && (
            <>
              <IonItem>
                <IonSearchbar
                  animated={true}
                  id="search"
                  placeholder="Find a team by name or TeamID"
                  value={searchValue}
                  // debounce={300}
                  onIonInput={onSearch}
                ></IonSearchbar>
              </IonItem>
              {/* {searchLoad && (
                <IonLoading
                  isOpen={searchLoad}
                  message={"Searching"}
                  spinner="lines"
                />
              )} */}
              {teamList && (
                <IonItem>
                  <IonList style={{ width: "100%" }}>
                    {teamList.map((el: any, i) => (
                      <IonItem
                        key={i}
                        button
                        onClick={() => selectTeam(el.teamId)}
                        detail={true}
                      >
                        <IonText>
                          <div style={{ fontSize: "1.1em", width: "100%" }}>
                            <span>{`(${el.teamId})`}</span>{" "}
                            <span>{el.teamName}</span>
                          </div>
                          <div style={{ fontSize: "0.7em", width: "100%" }}>
                            {obfuscateEmail(el.teamEmail)}
                          </div>
                        </IonText>
                      </IonItem>
                    ))}
                    {hasMoreResults && (
                      <IonItem button onClick={loadMoreResults}>
                        <IonText color="primary">
                          <div
                            style={{
                              fontSize: "0.9em",
                              textAlign: "center",
                              width: "100%",
                            }}
                          >
                            Show{" "}
                            {Math.min(
                              LOAD_MORE_COUNT,
                              allSearchResults.length - displayedResultsCount
                            )}{" "}
                            more results (
                            {allSearchResults.length - displayedResultsCount}{" "}
                            remaining)
                          </div>
                        </IonText>
                      </IonItem>
                    )}
                  </IonList>
                </IonItem>
              )}
            </>
          )}
          <Spacer size="30" />
          {team.newTeam && (
            <>
              <IonItem
                className={`${isValid.teamName && "ion-valid"} ${
                  isValid.teamName === false && "ion-invalid"
                } ${isTouched.teamName && "ion-touched"}`}
              >
                <IonLabel position="floating">Team Name</IonLabel>
                <IonInput
                  type="text"
                  name="teamName"
                  placeholder="Team Name"
                  value={team.teamName}
                  onIonChange={onChange}
                  onIonBlur={markTouched}
                />
                <IonNote slot="helper">Enter a team name</IonNote>
                <IonNote slot="error">Invalid team name</IonNote>
              </IonItem>
              <Spacer size="15" />
            </>
          )}

          {team.newTeam && (
            <>
              <IonItem
                className={`${isValid.teamEmail && "ion-valid"} ${
                  isValid.teamEmail === false && "ion-invalid"
                } ${isTouched.teamEmail && "ion-touched"}`}
              >
                <IonLabel position="floating">Team Email</IonLabel>
                <IonInput
                  type="email"
                  name="teamEmail"
                  placeholder="Team Email"
                  value={team.teamEmail}
                  onIonChange={onChange}
                  onIonBlur={markTouched}
                />
                <IonNote slot="helper">Enter a valid email (optional)</IonNote>
                <IonNote slot="error">Invalid email format</IonNote>
              </IonItem>
              <Spacer size="15" />
            </>
          )}
          {(team.newTeam || team.teamId) && (
            <>
              <IonItem
                className={`${isValid.playerCount && "ion-valid"} ${
                  isValid.playerCount === false && "ion-invalid"
                } ${isTouched.playerCount && "ion-touched"}`}
              >
                <IonLabel position="floating">Player Count</IonLabel>
                <IonInput
                  type="number"
                  name="playerCount"
                  placeholder="Player Count"
                  value={team.playerCount}
                  onIonChange={onChange}
                  onIonBlur={markTouched}
                />
                <IonNote slot="helper">
                  Enter the number of players on the team
                </IonNote>
                <IonNote slot="error">Invalid player count</IonNote>
              </IonItem>
              <Spacer size="15" />
            </>
          )}
        </IonContent>
        {team.newTeam && (
          <IonFooter>
            <IonItem>
              <IonButtons slot="end">
                <IonButton color="primary" onClick={registerNewTeam}>
                  Confirm
                </IonButton>
              </IonButtons>
            </IonItem>
          </IonFooter>
        )}
      </IonModal>
    </>
  );
};

const mapStateToProps = (state: any) => ({
  allTeams: state.teams.allTeams,
  teams: state.teams,
  quizConfig: state.quiz.quizConfig,
  user: state.user,
  quiz: state.quiz,
});

export default connect(mapStateToProps, { addTeam, registerTeam })(
  AddTeamModal
);
